import React, { FormEvent } from "react";
import { Package, FileText, AlertCircle, Image, Plus } from "lucide-react";
import { CheckInPurchaseOrderMapper } from "src/mappers";
import { ValidationErrors } from "src/errors";
import {
  CheckInPurchaseOrderFormDataKey,
  CheckInPurchaseOrderFormData,
  CheckInPurchaseOrderFormAction,
  OrderProductCheckInInputField,
  NonOrderProductCheckInInputField
} from "src/types";
import { PurchaseOrder, ReceivingDiscrepancy } from "src/types/database";
import { useScrollOnMessage } from "src/hooks";
import { POWarehouseCheckInTooltip } from "src/components/tooltips/warehouseCheckInsTooltips";
import { OrderProductCheckInInputTable, NonOrderProductCheckInInputTable } from "src/components/tables/inputTables";
import { MultiFileUpload, TextInputBox, TextInputBoxLarge } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";

interface CheckInPurchaseOrderFormProps {
  formData: CheckInPurchaseOrderFormData;
  purchaseOrderToCheckIn?: PurchaseOrder;
  existingPOReceivingDiscrepancy?: ReceivingDiscrepancy;
  validationErrors?: ValidationErrors<CheckInPurchaseOrderFormDataKey>;
  formLvlErrorMessage: string;
  handleInputChange: (
    field: CheckInPurchaseOrderFormDataKey,
    value: CheckInPurchaseOrderFormData[keyof CheckInPurchaseOrderFormData]
  ) => void;
  handleSubmit: (e: FormEvent) => Promise<void>;
  setAction: (action: CheckInPurchaseOrderFormAction) => void;
}

const CheckInPurchaseOrderForm: React.FC<CheckInPurchaseOrderFormProps> = ({
  formData,
  purchaseOrderToCheckIn,
  existingPOReceivingDiscrepancy,
  validationErrors,
  formLvlErrorMessage,
  handleInputChange,
  handleSubmit,
  setAction
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const handleSetProductCheckIns: React.Dispatch<React.SetStateAction<OrderProductCheckInInputField[]>> = (
    updatedProductCheckIns
  ) => {
    handleInputChange("orderProductCheckIns", updatedProductCheckIns as OrderProductCheckInInputField[]);
  };

  const handleSetExtraProductCheckIns: React.Dispatch<React.SetStateAction<NonOrderProductCheckInInputField[]>> = (
    updatedExtraProductCheckIns
  ) => {
    handleInputChange("nonOrderProductCheckIns", updatedExtraProductCheckIns as NonOrderProductCheckInInputField[]);
  };

  const hasWarehouseSplit = formData.orderProductCheckIns.some((p) => p.hasWarehouseSplit);

  const handleAddExtraProductCheckIn = () => {
    handleInputChange("nonOrderProductCheckIns", [
      ...formData.nonOrderProductCheckIns,
      CheckInPurchaseOrderMapper.initNonOrderProductCheckInInputField(hasWarehouseSplit)
    ]);
  };

  const totalExpectedBoxesQty = purchaseOrderToCheckIn?.shipmentUnits?.reduce(
    (total, boxesShipmentUnit) => total + (parseInt(boxesShipmentUnit.quantity.toString(), 10) || 0),
    0
  );

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-4 sm:px-5 lg:px-6">
      <div className="mx-auto">
        <form onSubmit={handleSubmit} autoComplete="off" className="p-4">
          <div className="space-y-4">
            {/* Search Section */}
            <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
              <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                <Package className="w-4 h-4 mr-2" />
                Search
              </h3>
              <TextInputBox
                id="asn"
                placeholder="ASN or PO ID"
                onChange={(value) => {
                  handleInputChange("asn", value.toUpperCase());
                }}
                value={formData.asn}
              />
              <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("asn")} />
            </div>

            {purchaseOrderToCheckIn && (
              <>
                {/* Purchase Order Info Tooltip */}
                <POWarehouseCheckInTooltip
                  origin={purchaseOrderToCheckIn.origin}
                  purchaseOrderId={purchaseOrderToCheckIn.id}
                  totalExpectedBoxesQty={totalExpectedBoxesQty}
                />

                {/* Order Products Section */}
                <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                  <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Order Products
                  </h3>
                  <OrderProductCheckInInputTable
                    orderProductCheckIns={formData.orderProductCheckIns}
                    existingPOReceivingDiscrepancy={existingPOReceivingDiscrepancy}
                    setOrderProductCheckIns={handleSetProductCheckIns}
                  />
                  <ValidationErrorMessage
                    message={validationErrors?.getErrorMessageByFormDataKey("orderProductCheckIns")}
                  />
                </div>

                {/* Extra Products Section */}
                {formData.nonOrderProductCheckIns.length > 0 && (
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      Extra Products
                    </h3>
                    <NonOrderProductCheckInInputTable
                      nonOrderProductCheckIns={formData.nonOrderProductCheckIns}
                      setNonOrderProductCheckIns={handleSetExtraProductCheckIns}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("nonOrderProductCheckIns")}
                    />
                  </div>
                )}

                {/* Add Extra Products Button */}
                <div className="flex justify-center">
                  <button
                    onClick={handleAddExtraProductCheckIn}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                    type="button"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Extra Products (Optional)</span>
                  </button>
                </div>

                {/* Defects Section */}
                <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                  <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Defects Report
                  </h3>
                  <TextInputBoxLarge
                    id="defects"
                    placeholder="Report Defects (Optional)"
                    onChange={(value) => handleInputChange("defects", value)}
                    value={formData.defects}
                    maxLength={1000}
                  />
                  <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("defects")} />
                </div>

                {/* Images Upload Section */}
                <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                  <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                    <Image className="w-4 h-4 mr-2" />
                    Check-in Images
                  </h3>
                  <MultiFileUpload
                    uploadText="Upload Images (Optional)"
                    uploadedFiles={formData.checkInImages}
                    onFileChange={(files) => handleInputChange("checkInImages", files ?? [])}
                    fileFormat="image/jpeg, image/png, image/gif, image/webp"
                  />
                  <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("checkInImages")} />
                </div>
              </>
            )}

            {/* Form Level Error */}
            <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

            {/* Submit Button */}
            <div className="flex items-center gap-4">
              <button
                className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                disabled={!formData.asn}
                onClick={() => setAction(!purchaseOrderToCheckIn ? "FETCH" : "CHECK_IN")}
                type="submit"
              >
                {!purchaseOrderToCheckIn ? "Search" : "Check-in"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export { CheckInPurchaseOrderForm };
